import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type CampaignBudgetResult } from '@whinerp/client';
import { formatMoney, todayIn } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Planned against actual (G-165). Spend is recorded as lines; once there are
 * lines the campaign's cost is their total, which is what cost-per-lead and
 * acquisition cost read.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function CampaignBudgetPanel({ campaignId }: { campaignId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const key = ['campaign-budget', campaignId];
  const budget = useQuery<CampaignBudgetResult, ApiError>({ queryKey: key, queryFn: () => api.campaignBudget({ path: { campaignId } }) });
  const [planned, setPlanned] = useState<string | null>(null);
  const [line, setLine] = useState({ amount: '', description: '', supplier: '' });
  const put = (result: CampaignBudgetResult) => {
    queryClient.setQueryData(key, result);
    void queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
  };
  const setBudget = useMutation({
    mutationFn: (value: string | null) => api.setCampaignBudget({ path: { campaignId }, body: { budget: value } }),
    onSuccess: (result) => {
      put(result);
      setPlanned(null);
    },
  });
  const spend = useMutation({
    mutationFn: () =>
      api.recordCampaignSpend({
        path: { campaignId },
        body: {
          spentOn: todayIn(format),
          amount: line.amount,
          currency: budget.data?.currency ?? 'AED',
          description: line.description,
          supplier: line.supplier || null,
        },
      }),
    onSuccess: (result) => {
      put(result);
      setLine({ amount: '', description: '', supplier: '' });
    },
  });
  const remove = useMutation({
    mutationFn: (spendId: string) => api.removeCampaignSpend({ body: { spendId } }),
    onSuccess: put,
  });

  if (!budget.data) return null;
  const view = budget.data;
  const money = (amount: string | null) => (amount === null ? t('budget.unknown') : formatMoney(amount, format, { currency: view.currency }));

  return (
    <Panel data-region="campaign-budget">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('budget.title')}</SectionLabel>
        <dl className="grid grid-cols-3 gap-2 text-dense">
          <div><dt className="text-meta text-ink-meta">{t('budget.planned')}</dt><dd data-budget-planned>{money(view.budget)}</dd></div>
          <div><dt className="text-meta text-ink-meta">{t('budget.actual')}</dt><dd data-budget-actual>{money(view.actual)}</dd></div>
          <div>
            <dt className="text-meta text-ink-meta">{t('budget.remaining')}</dt>
            <dd data-budget-remaining className={view.over ? 'text-danger-text' : 'text-ink'}>
              {view.remaining === null ? '-' : view.over ? t('budget.over', { values: { amount: money(view.remaining.replace('-', '')) } }) : money(view.remaining)}
            </dd>
          </div>
        </dl>
        <form className="flex flex-wrap items-end gap-2" data-budget-form
          onSubmit={(event) => {
            event.preventDefault();
            setBudget.mutate(planned === null || planned.trim() === '' ? null : planned.trim());
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('budget.setPlanned')}
            <input className={`${INPUT} w-36`} inputMode="decimal" data-budget-input value={planned ?? view.budget ?? ''}
              onChange={(event) => setPlanned(event.target.value)} />
          </label>
          <Button size="small" type="submit" data-budget-save disabled={setBudget.isPending}>{t('budget.save')}</Button>
        </form>

        {view.lines.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-spend-lines>
            {view.lines.map((entry) => (
              <li key={entry.spendId} data-spend={entry.spendId} className="flex flex-wrap items-center gap-2">
                <span className="text-ink-meta">{entry.spentOn}</span>
                <span className="text-ink">{entry.description}</span>
                {entry.supplier ? <span className="text-ink-600">{entry.supplier}</span> : null}
                <span className="ms-auto text-ink">{money(entry.amount)}</span>
                <Button size="small" data-spend-remove={entry.spendId} onClick={() => remove.mutate(entry.spendId)}>{t('budget.remove')}</Button>
              </li>
            ))}
          </ul>
        ) : null}
        {view.children.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-budget-children>
            {view.children.map((child) => (
              <li key={child.campaignId} className="flex gap-2">
                <span className="text-ink">{child.name}</span>
                <span className="ms-auto text-ink-600">
                  {child.counted ? money(child.cost) : child.cost === null ? t('budget.unknown') : t('budget.otherCurrency', { values: { currency: child.currency } })}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <form className="flex flex-wrap items-end gap-2" data-spend-form
          onSubmit={(event) => {
            event.preventDefault();
            spend.mutate();
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('budget.amount')}
            <input className={`${INPUT} w-32`} required inputMode="decimal" data-spend-amount value={line.amount}
              onChange={(event) => setLine({ ...line, amount: event.target.value })} />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-meta text-ink-meta">
            {t('budget.description')}
            <input className={INPUT} required maxLength={300} data-spend-description value={line.description}
              onChange={(event) => setLine({ ...line, description: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('budget.supplier')}
            <input className={INPUT} maxLength={200} data-spend-supplier value={line.supplier}
              onChange={(event) => setLine({ ...line, supplier: event.target.value })} />
          </label>
          <Button size="small" type="submit" data-spend-save disabled={spend.isPending}>{t('budget.record')}</Button>
        </form>
        {[setBudget.error, spend.error, remove.error].map((error, index) =>
          error ? <p key={index} className="text-dense text-danger-text" data-budget-error>{errorText(error as ApiError)}</p> : null,
        )}
      </div>
    </Panel>
  );
}
